import React, { useEffect } from 'react';
import { MapPin, Phone, Clock, Mail, Facebook, Instagram } from 'lucide-react';
import { businessInfo, gallery } from '../data/mockData';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-burgundy text-cream py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6">
            Contatti
          </h1>
          <p className="text-xl font-serif max-w-3xl mx-auto leading-relaxed text-cream/90">
            Vieni a trovarci a Santarcangelo di Romagna o contattaci per informazioni
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Details */}
            <div>
              <h2 className="font-serif text-4xl font-bold text-burgundy mb-8">
                Come Raggiungerci
              </h2>

              <div className="space-y-6">
                {/* Address */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center">
                      <MapPin size={24} className="text-burgundy" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-bold text-burgundy mb-2">
                        Indirizzo
                      </h3>
                      <p className="text-wood-brown font-serif">
                        {businessInfo.location.address}<br />
                        {businessInfo.location.postalCode} {businessInfo.location.city} {businessInfo.location.province}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center">
                      <Phone size={24} className="text-burgundy" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-bold text-burgundy mb-2">
                        Telefono
                      </h3>
                      <a 
                        href={`tel:${businessInfo.contact.phone}`}
                        className="text-wood-brown font-serif hover:text-burgundy transition-colors block"
                      >
                        {businessInfo.contact.phone}
                      </a>
                      <a 
                        href={`https://wa.me/${businessInfo.contact.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-wood-brown font-serif hover:text-burgundy transition-colors block mt-1"
                      >
                        WhatsApp: {businessInfo.contact.whatsapp}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Opening Hours */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center">
                      <Clock size={24} className="text-burgundy" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-bold text-burgundy mb-2">
                        Orari di Apertura
                      </h3>
                      <div className="text-wood-brown font-serif">
                        <p className="font-medium">Dal Lunedì al Sabato</p>
                        <p className="mb-2">{businessInfo.hours.weekdays}</p>
                        <p className="font-medium">Domenica</p>
                        <p>{businessInfo.hours.weekend}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="font-serif text-xl font-bold text-burgundy mb-4">
                    Seguici sui Social
                  </h3>
                  <div className="flex space-x-4">
                    <a 
                      href={businessInfo.contact.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 bg-burgundy/10 text-burgundy px-4 py-3 rounded-md hover:bg-burgundy hover:text-cream transition-all font-serif"
                    >
                      <Facebook size={20} />
                      <span>Facebook</span>
                    </a>
                    <a 
                      href={businessInfo.contact.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 bg-burgundy/10 text-burgundy px-4 py-3 rounded-md hover:bg-burgundy hover:text-cream transition-all font-serif"
                    >
                      <Instagram size={20} />
                      <span>Instagram</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div>
              <h2 className="font-serif text-4xl font-bold text-burgundy mb-8">
                Dove Siamo
              </h2>
              <div className="bg-white p-2 rounded-lg shadow-lg">
                <div className="w-full h-[600px] rounded-lg overflow-hidden">
                  <iframe
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2867.8!2d${businessInfo.location.coordinates.lng}!3d${businessInfo.location.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132cb5c7c8f8e8e1%3A0x8f8f8f8f8f8f8f8f!2s${encodeURIComponent(businessInfo.location.fullAddress)}!5e0!3m2!1sit!2sit!4v1234567890123!5m2!1sit!2sit`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mappa Salumificio Lombardi"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-beige">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-burgundy text-center mb-12">
            Galleria Fotografica
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {gallery.map((photo, index) => (
              <div 
                key={index}
                className="aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <img 
                  src={photo.url} 
                  alt={photo.alt}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-burgundy text-cream">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">
            Ti Aspettiamo!
          </h2>
          <p className="text-lg font-serif max-w-2xl mx-auto leading-relaxed text-cream/90 mb-8">
            Vieni a scoprire i sapori autentici della tradizione romagnola nel nostro salumificio. 
            La qualità e l'ospitalità della famiglia Lombardi ti aspettano.
          </p>
          <a 
            href="tel:0541750295"
            className="inline-flex items-center justify-center bg-cream text-burgundy px-10 py-4 rounded-md font-serif font-medium text-lg transition-all duration-300 hover:bg-beige hover:shadow-xl hover:scale-105"
          >
            <Phone size={20} className="mr-2" />
            Chiamaci Ora
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
